import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, useTheme } from "@mui/material/styles";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 0 };

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
};

const formatUtcDate = (value) => {
  if (!value) return "—";
  return new Date(`${value}T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatRange = (start, end) => {
  if (!start) return "—";
  return `${formatUtcDate(start)} – ${formatUtcDate(end || start)}`;
};

const formatMonth = (value) => {
  if (!value) return "";
  return new Date(`${value}-01T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
};

const formatStepValue = (step) => {
  const digits = step.unit === "units" ? 0 : 1;
  const suffix = step.unit === "units" ? " units" : "/day";
  return `${formatNumber(step.value, digits)}${suffix}`;
};

const qualityColor = (theme, level) => {
  if (level === "high") return theme.palette.success.main;
  if (level === "low") return theme.palette.error.main;
  return theme.palette.warning.main;
};

const statusTone = (theme, label) => {
  if (label === "USED") return theme.palette.primary.main;
  if (label === "EXCLUDED") return theme.palette.warning.main;
  if (label === "OBSERVED") return theme.palette.info.main;
  return theme.palette.text.secondary;
};

const Section = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 700,
  fontSize: 13,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const isForecast = point.forecast !== null && point.forecast !== undefined;
  const noEvidence = point.observed === null && !isForecast;
  return (
    <Paper
      elevation={3}
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 1,
        bgcolor: "rgba(33, 33, 33, 0.94)",
        color: "#fff",
        minWidth: 200,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {formatMonth(label)}
      </Typography>
      {noEvidence ? (
        <Typography sx={{ fontSize: 11, color: "#ffe082" }}>
          Insufficient evidence to represent demand. Not treated as zero.
        </Typography>
      ) : isForecast ? (
        <>
          <Typography sx={{ fontSize: 11 }}>
            Forecast: {formatNumber(point.forecast)} units
          </Typography>
          {point.projected_daily != null && (
            <Typography sx={{ fontSize: 11 }}>
              {formatNumber(point.projected_daily, 1)}/day
              {point.days_in_period
                ? ` × ${point.days_in_period} days`
                : ""}
            </Typography>
          )}
        </>
      ) : (
        <>
          {point.observed !== null && point.observed !== undefined && (
            <Typography sx={{ fontSize: 11 }}>
              Observed sales: {formatNumber(point.observed)}
            </Typography>
          )}
          {point.adjusted !== null &&
            point.adjusted !== point.observed && (
              <Typography sx={{ fontSize: 11 }}>
                Lost Sales adjusted: {formatNumber(point.adjusted)}
              </Typography>
            )}
          {point.calendar_days > 0 && (
            <Typography sx={{ fontSize: 11, color: "#cfd8dc", mt: 0.4 }}>
              Usable days: {point.usable_days} / {point.calendar_days}
              {point.stockout_days ? ` · Stockout: ${point.stockout_days}` : ""}
              {point.event_days ? ` · Event: ${point.event_days}` : ""}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

const ForecastBridge = ({ steps }) => {
  if (!steps.length) return null;
  return (
    <Box>
      <Section>How we got here</Section>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        {steps.map((step, index) => {
          const previous = steps[index - 1];
          const sameUnit = previous && previous.unit === step.unit;
          const showDelta =
            sameUnit &&
            step.delta != null &&
            Math.abs(step.delta) >= 0.05 &&
            Math.abs(previous.value + step.delta - step.value) < 0.1;
          return (
            <Box key={step.key}>
              {index > 0 && (
                <Box sx={{ pl: 1.5, py: 1.1, borderLeft: "2px solid", borderColor: "divider", ml: 0.75 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "Poppins", fontWeight: 600, color: "text.secondary" }}
                  >
                    ↓ {step.transition || step.label}
                    {showDelta
                      ? ` ${step.delta > 0 ? "+" : ""}${formatNumber(step.delta, 1)}${step.unit || ""}`
                      : ""}
                  </Typography>
                  {step.why && (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "Poppins", color: "text.secondary", lineHeight: 1.5, mt: 0.25 }}
                    >
                      {step.why}
                    </Typography>
                  )}
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ fontFamily: "Poppins", color: "text.secondary" }}>
                  {step.label}
                </Typography>
                <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18 }}>
                  {formatStepValue(step)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

const InHouseForecastReport = ({ item }) => {
  const theme = useTheme();
  const analysis = item?.in_house?.analysis || {};
  const story = item?.in_house?.story || {};
  const chart = item?.in_house?.chart || {};
  const drivers = item?.in_house?.drivers || story.drivers || [];
  const pipeline = item?.in_house?.pipeline || [];
  const methodology = item?.in_house?.methodology || {};
  const periods =
    item?.in_house?.forecast?.periods || item?.forecast_adjusted || [];
  const quality = story.quality || {};
  const historyStart = analysis.history_start || story.history?.history_start;
  const historyEnd = analysis.history_end || story.history?.history_end;
  const availableDays = analysis.available_days || story.history?.available_days;
  const accent = qualityColor(theme, quality.level);
  const observedColor = theme.palette.error.main;
  const adjustedColor = theme.palette.secondary.main;
  const forecastColor = theme.palette.success.main;
  const grainLabel =
    item.in_house?.grain_label ||
    (item.in_house?.analysis_level === "product"
      ? `Product pooled across ${item.in_house?.child_sku_count || item.in_house?.child_skus?.length || 0} child SKUs × warehouse`
      : "SKU × warehouse");
  const monthlyData = useMemo(() => chart.monthly || [], [chart.monthly]);
  const forecastStartMonth = monthlyData.find(
    (row) => row.forecast !== null && row.forecast !== undefined,
  )?.month;
  const showAdjusted = monthlyData.some(
    (row) => row.adjusted !== null && row.adjusted !== row.observed,
  );
  const trendApplied = analysis.trend?.applied;
  const monthShiftReason = trendApplied
    ? `Monthly totals vary with both projected daily demand and calendar length. ${
        analysis.trend?.direction === "decreasing"
          ? "Later months are lower because a declining trend was applied."
          : "Later months are higher because an increasing trend was applied."
      }`
    : "Monthly totals vary with both projected daily demand and calendar length. Without an applied trend, month-to-month differences are calendar length, not a demand change.";
  const extraWarnings = [
    ...(quality.data_limitations || []),
    ...(quality.forecast_limitations || []),
  ].filter((text) => text && text !== quality.summary && !quality.summary?.includes(text));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.25 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Chip
          size="small"
          label={(quality.level || "medium").replace(/^\w/, (c) => c.toUpperCase())}
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            bgcolor: `${accent}16`,
            color: accent,
            border: `1px solid ${accent}44`,
          }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={grainLabel}
          sx={{ fontFamily: "Poppins" }}
        />
        <Chip size="small" variant="outlined" label="In-house" sx={{ fontFamily: "Poppins" }} />
        {item.in_house?.warehouse_name && (
          <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary" }}>
            {item.in_house.warehouse_name}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, mb: 0.25 }}>
          Demand history
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary", mb: 1.5 }}>
          {formatRange(historyStart, historyEnd)}
          {availableDays ? ` · ${formatNumber(availableDays)} calendar days` : ""}
          {analysis.usable_days != null
            ? ` · ${formatNumber(analysis.usable_days)} usable`
            : ""}
          {analysis.warehouse_coverage_end &&
          analysis.warehouse_coverage_end < historyEnd
            ? ` · last warehouse order ${formatUtcDate(analysis.warehouse_coverage_end)}`
            : ""}
        </Typography>
        <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2, height: { xs: 300, sm: 380 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                minTickGap={18}
                tick={{ fontSize: 11, fontFamily: "Poppins" }}
              />
              <YAxis
                tickFormatter={(value) => formatNumber(value)}
                width={46}
                tick={{ fontSize: 11, fontFamily: "Poppins" }}
              />
              <RechartsTooltip content={<ChartTip />} />
              {(chart.bands || [])
                .filter((band) => band.type === "gap")
                .map((band) => (
                  <ReferenceArea
                    key={`gap-${band.start}`}
                    x1={band.start.slice(0, 7)}
                    x2={band.end.slice(0, 7)}
                    fill={theme.palette.action.disabled}
                    fillOpacity={0.1}
                  />
                ))}
              {(chart.bands || [])
                .filter((band) => band.type === "event")
                .map((band) => (
                  <ReferenceArea
                    key={`event-${band.start}`}
                    x1={band.start.slice(0, 7)}
                    x2={band.end.slice(0, 7)}
                    fill={theme.palette.warning.main}
                    fillOpacity={0.14}
                  />
                ))}
              {(chart.bands || [])
                .filter((band) => band.type === "stockout")
                .map((band) => (
                  <ReferenceArea
                    key={`so-${band.start}`}
                    x1={band.start.slice(0, 7)}
                    x2={band.end.slice(0, 7)}
                    fill={theme.palette.error.main}
                    fillOpacity={0.08}
                  />
                ))}
              {forecastStartMonth && (
                <ReferenceArea
                  x1={forecastStartMonth}
                  x2={monthlyData[monthlyData.length - 1]?.month}
                  fill={forecastColor}
                  fillOpacity={0.05}
                />
              )}
              <Bar
                dataKey="observed"
                name="Observed sales"
                fill={observedColor}
                radius={[3, 3, 0, 0]}
                maxBarSize={26}
                fillOpacity={0.85}
              />
              {showAdjusted && (
                <Line
                  type="monotone"
                  dataKey="adjusted"
                  name="Lost Sales adjusted"
                  stroke={adjustedColor}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              )}
              <Line
                type="monotone"
                dataKey="forecast"
                name="Forecast"
                stroke={forecastColor}
                strokeWidth={2.5}
                strokeDasharray="5 4"
                dot={{ r: 3.5, fill: forecastColor }}
                connectNulls={false}
              />
              <Legend wrapperStyle={{ fontFamily: "Poppins", fontSize: 12 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Paper>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
          {[
            { color: observedColor, label: "Observed sales" },
            showAdjusted && { color: adjustedColor, label: "Lost Sales adjusted" },
            { color: forecastColor, label: "Forecast" },
            { color: theme.palette.warning.main, label: "Events / promotions" },
            { color: theme.palette.error.main, label: "Stockouts" },
            { color: theme.palette.action.disabled, label: "Unavailable / gaps" },
          ]
            .filter(Boolean)
            .map((entry) => (
              <Box key={entry.label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.4, bgcolor: entry.color, opacity: 0.85 }} />
                <Typography variant="caption" sx={{ fontFamily: "Poppins", color: "text.secondary" }}>
                  {entry.label}
                </Typography>
              </Box>
            ))}
        </Box>
        <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary", mt: 1, lineHeight: 1.55 }}>
          {chart.caption ||
            "Observed is what actually sold. Lost Sales adjusted adds estimated recoverable demand on applicable stockout days only. Event days are shown in historical demand but excluded from baseline fitting."}
        </Typography>
      </Box>

      <Box>
        <Section>Forecast</Section>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: `repeat(${Math.min(Math.max(periods.length, 1), 4)}, minmax(0, 1fr))`,
            },
            gap: 1.25,
            mb: 1.25,
          }}
        >
          {periods.map((point) => (
            <Paper key={String(point.date)} variant="outlined" sx={{ p: 1.75, borderRadius: 1.5, textAlign: "center" }}>
              <Typography variant="caption" sx={{ fontFamily: "Poppins", fontWeight: 600, color: "text.secondary" }}>
                {new Date(point.date).toLocaleDateString(undefined, {
                  month: "long",
                  year: "numeric",
                  timeZone: "UTC",
                })}
              </Typography>
              <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "text.primary" }}>
                {formatNumber(point.predicted_qty)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "Poppins" }}>
                {point.projected_daily != null
                  ? `${formatNumber(point.projected_daily, 1)}/day × ${point.days_in_period}`
                  : "units"}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary" }}>
          {monthShiftReason}
        </Typography>
      </Box>

      <ForecastBridge steps={pipeline} />

      {drivers.length > 0 && (
        <Box>
          <Section>What actually mattered</Section>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1,
            }}
          >
            {drivers.map((driver) => {
              const tone = statusTone(theme, driver.status_label);
              return (
                <Paper
                  key={driver.key}
                  variant="outlined"
                  sx={{ p: 1.6, borderRadius: 1.5, borderLeft: `3px solid ${tone}` }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={driver.status_label || (driver.status === "applied" ? "USED" : "OBSERVED")}
                      sx={{
                        height: 20,
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        fontSize: 10,
                        bgcolor: `${tone}14`,
                        color: tone,
                      }}
                    />
                    <Typography sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 13.5 }}>
                      {driver.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary", lineHeight: 1.5 }}>
                    {driver.explanation}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {(quality.summary || extraWarnings.length > 0) && (
        <Box>
          <Section>Data quality</Section>
          {quality.summary && (
            <Alert
              severity={quality.level === "low" ? "warning" : quality.level === "high" ? "success" : "info"}
              sx={{ mb: extraWarnings.length ? 1 : 0, fontFamily: "Poppins", borderRadius: 1.5 }}
            >
              {quality.summary}
            </Alert>
          )}
          {extraWarnings.map((itemText) => (
            <Alert key={itemText} severity="info" sx={{ mb: 1, fontFamily: "Poppins", borderRadius: 1.5 }}>
              {itemText}
            </Alert>
          ))}
        </Box>
      )}

      <Accordion
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontFamily: "Poppins", fontWeight: 700 }}>Methodology</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: "Poppins", color: "text.secondary", mb: 1.25 }}>
            {methodology.model}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {(methodology.bullets || []).map((bullet) => (
              <Typography key={bullet} component="li" variant="body2" sx={{ fontFamily: "Poppins", mb: 0.75, lineHeight: 1.55 }}>
                {bullet}
              </Typography>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default InHouseForecastReport;
